import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getScrollableState } from 'reducers/scrollable';
import { scrollableScrollLeft, scrollableScrollRight, setScrollableOffset } from 'actions/scrollable';
import HorizontalScroll from 'components/HorizontalScroll/HorizontalScroll';
import { usePrevious } from 'utils/hooks';
import { ThemeContext } from 'react-fela';
import { responsiveRem2Px } from 'utils/helpers';
import { ItemContainer, ArrowContainer, Arrow, ScrollableContainer } from './Styles';
import NextIcon from 'components/Icons/NextIcon';
import useResizeObserver from 'use-resize-observer';

const Scrollable = ({
  id,
  pageId,
  children,
  loadMore,
  hasMoreLoad,
  itemWidthPx = 0,
  itemWidthRem = 0,
  scrollToNewItem,
}) => {
  const {
    hasNext,
    hasPrev,
  } = useSelector(state => getScrollableState(state, pageId, id), shallowEqual);
  const dispatch = useDispatch();
  const theme = useContext(ThemeContext);
  const itemContainerMarginLeftRem = parseFloat(theme.margin.medium);
  const itemContainerMarginRightRem = 0.125;
  const itemContainerMarginRem = itemContainerMarginLeftRem + itemContainerMarginRightRem;

  const previousFirstChildKey = usePrevious(children?.[0]?.key);
  const itemContainerRef = useRef();
  const [arrowWrapperPosition, setArrowWrapperPosition] = useState({});
  const { height: itemContainerHeight  } = useResizeObserver({ ref: itemContainerRef });

  useEffect(() => {
    if (
      scrollToNewItem
      && children.length > 1
      && previousFirstChildKey
      && children[0].key !== previousFirstChildKey
      && children[1].key === previousFirstChildKey
    ) {
      // new item is added
      // scroll collection to 0 position after rendering
      setTimeout(() => dispatch(setScrollableOffset(pageId, id, 0)));
    }
  }, [scrollToNewItem, children, previousFirstChildKey]);

  useEffect(() => {
    const anchorRect = itemContainerRef.current?.getBoundingClientRect();
    const imgs = itemContainerRef.current?.getElementsByTagName('img');
    if (imgs && imgs.length > 0 && anchorRect) {
      const imgRect = imgs[0].getBoundingClientRect();
      const top = imgRect.top - anchorRect.top;
      const { height } = imgRect;
      setArrowWrapperPosition({ top, height });
    }
  }, [itemContainerHeight]);

  const items = React.Children.map(children, (child) => {
    if (!child) {
      return null;
    }

    return (
      <ItemContainer
        innerRef={itemContainerRef}
        key={child.key || undefined}
        marginLeft={`${itemContainerMarginLeftRem}rem`}
        marginRight={`${itemContainerMarginRightRem}rem`}
      >
        {child}
      </ItemContainer>
    );
  });

  return (
    <ScrollableContainer>
      <ArrowContainer
        position="left"
        isVisible={hasPrev}
        className="arrow"
        onClick={() => dispatch(scrollableScrollLeft(pageId, id))}
        data-cy="left-arrow"
      >
        <Arrow
          top={arrowWrapperPosition.top}
          height={arrowWrapperPosition.height}
        >
          <NextIcon />
        </Arrow>
      </ArrowContainer>

      <HorizontalScroll
        pageId={pageId}
        id={id}
        loadMore={loadMore}
        hasMoreLoad={hasMoreLoad}
        itemWidthRem={itemWidthRem || itemWidthPx
          ? itemWidthRem + itemContainerMarginRem
          : itemWidthRem}
        itemWidthPx={itemWidthPx}
        itemMarginLeftPx={responsiveRem2Px(itemContainerMarginLeftRem)}
        itemMarginRightPx={responsiveRem2Px(itemContainerMarginRightRem)}
        spacer
      >
        {items}
      </HorizontalScroll>

      <ArrowContainer
        position="right"
        isVisible={hasNext}
        className="arrow"
        onClick={() => dispatch(scrollableScrollRight(pageId, id))}
        data-cy="right-arrow"
      >
        <Arrow top={arrowWrapperPosition.top} height={arrowWrapperPosition.height}>
          <NextIcon right />
        </Arrow>
      </ArrowContainer>
    </ScrollableContainer>
  );
};

Scrollable.propTypes = {
  id: PropTypes.string.isRequired,
  pageId: PropTypes.string.isRequired,
  scrollToNewItem: PropTypes.bool,
  children: PropTypes.node.isRequired,
  loadMore: PropTypes.func,
  hasMoreLoad: PropTypes.bool,
  itemWidthRem: PropTypes.number,
  itemWidthPx: PropTypes.number,
};

export default Scrollable;
